import { css } from '@emotion/react';
import { useDevToolsStore } from '@frontend/devtools';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, SearchField } from '@frontend/design-system';
import { AddToListButton } from './add-to-list-button';
import { CountInfo } from './count-info';

interface Props {
  locationId: string;
  selectedCount: number;
  fetchedCount: number;
  totalCount: number;
  searchFieldProps: Omit<React.ComponentProps<typeof SearchField>, 'name'>;
  hasAccordionWrapper: boolean;
  onSelectAllToggle: () => void;
  onRefreshList?: () => void;
}

export const WaitListHeader = ({
  locationId,
  selectedCount,
  fetchedCount,
  totalCount,
  searchFieldProps,
  hasAccordionWrapper = false,
  onSelectAllToggle,
  onRefreshList,
}: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const isDebugModeOn = useDevToolsStore((state) => state.options.isDebugModeOn);

  const hasSelection = selectedCount && selectedCount > 0;
  const isAllSelected = hasSelection && selectedCount === fetchedCount;

  return (
    <header css={[headerStyle, hasAccordionWrapper && { padding: theme.spacing(2, 2, 0.25, 2) }]}>
      {!hasAccordionWrapper && <CountInfo selectedCount={selectedCount} totalCount={totalCount} />}
      <SearchField name='search' {...searchFieldProps} />
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant='tertiary'
          iconName='select-all'
          css={{ padding: theme.spacing(0.25), zIndex: 1 }}
          onClick={onSelectAllToggle}
        >
          {isAllSelected ? t('Deselect All') : t('Select All')}
        </Button>
        {isDebugModeOn && (
          <Button
            size='small'
            variant='secondary'
            iconName='refresh'
            color={theme.colors.neutral70}
            onClick={onRefreshList}
            css={{ marginLeft: 'auto', marginRight: theme.spacing(1), zIndex: 1 }}
          />
        )}
        <AddToListButton locationId={locationId} show={!hasSelection} padding={theme.spacing(0.25)} />
      </div>
    </header>
  );
};

const headerStyle = css({
  padding: theme.spacing(0.25, 3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  marginTop: theme.spacing(2),
});
